<script lang="ts">
    import { onMount } from "svelte";
    import { v4 } from "uuid";
    import { certificationPricingStore, orderFormStore, infoRequestStore } from "$lib/stores/store";
    import { captureInfoRequest } from "$lib/firebase";
    import { orderTypeToEnglish } from "$lib/utils/orderUtils.js";
    import { exotics } from "../../../lib/assets/lists/exotics";
    import ogImage from "$lib/assets/images/og-image.png";

    // Components
    import PriceCards from "$lib/Components/Order/PriceCards.svelte";
    import RepairCards from "$lib/Components/Order/RepairCards.svelte";
    import WhyC2 from "$lib/Components/Order/WhyC2.svelte";
    import CTAImageSection from "$lib/Components/Order/CTAImageSection.svelte";
    import SectionQuote from "$lib/Components/Order/SectionQuote.svelte";
    import PaymentCards from "$lib/Components/Order/PaymentCards.svelte";
    import Testimoninals from "$lib/Components/Order/Testimoninals.svelte";
    import ReferralCheckout from "$lib/Components/Referral/ReferralCheckout.svelte";
    import PriceCard from "$lib/Components/referrer/PriceCard.svelte";
    import OrderFormStoreDisplay from "$lib/Components/Debug/OrderFormStoreDisplay.svelte";
    import OrderSummary from "$lib/Components/referrer/OrderSummary.svelte";

    export let data;
    const { isValid, order } = data;

    // Order details
    let order_id = order.o_id;
    let make = order.make;
    let model = order.model;
    let year = order.year;
    let order_type_id = determineOrderType(order.year, order.make, order.model);
    let orderType: string = orderTypeToEnglish(order_type_id);

    let uniqueIdentifier: string;
    let pricing: any;
    let step = 1;
    let formSubmitted = false;

    function determineOrderType(vehicle_year: number, vehicle_make: string, vehicle_model: string) {
        const currentYear = new Date().getFullYear();

        if (!vehicle_year || !vehicle_make || !vehicle_model) {
            console.log("Need make, model and year");
            return;
        }

        if (vehicle_year < currentYear - 20) {
            return "3"; // classic
        }

        if (exotics.includes(vehicle_model)) {
            return "4"; // exotic
        }

        return "2"; // standard
    }

    onMount(() => {
        if (!isValid) {
            window.location.href = "/referral";
            return;
        }

        window.scrollTo(0, 0);
        uniqueIdentifier = v4();
        pricing = certificationPricingStore[determineOrderType(order.year, order.make, order.model)];

        // Initialize order form store
        $orderFormStore = {
            ...$orderFormStore,
            c_phone: order.c_phone1,
            vehicle_make: order.make,
            vehicle_model: order.model,
            vehicle_year: order.year,
            order_type: determineOrderType(order.year, order.make, order.model),
            isAnnually: false,
            isCertified: true,
            certificationPriceInCents: pricing?.cents_monthly,
            vehicle_address1: order.v_address1,
            lemonSquadPriceInCents: 0,
            stripe_price_id: certificationPricingStore[order_type_id].stripe_id_monthly,
            lemonsquad_order_id: order.o_id,
        };

        $infoRequestStore.order_id = order_id;
    });

    async function submitInfoRequestForm() {
        try {
            await captureInfoRequest($infoRequestStore);
            formSubmitted = true;
        } catch (error) {
            console.error("Error submitting info request:", error);
        }
    }

    async function handleCheckout() {
        if (!$orderFormStore.carConfirmed) {
            alert("Please confirm the vehicle you want to purchase a subscription for");
            return;
        }

        $orderFormStore.uuid = uniqueIdentifier;

        try {
            const response = await fetch("/api/referral-order", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ $orderFormStore }),
            });

            const result = await response.json();
            const sessionUrl = result.stripeSessionUrl;

            if (sessionUrl) {
                window.location.href = sessionUrl;
            } else {
                console.error("Failed to create Stripe session:", result.error);
            }
        } catch (error) {
            console.error("Error processing checkout:", error);
        }
    }
</script>

<svelte:head>
    <title>Inspection Guarantee Pricing | {year} {make} {model}</title>
    <meta property="og:title" content="Inspection Guarantee | {year} {make} {model}" />
    <meta property="og:description" content="View Inspection Guarantee pricing details for {year} {make} {model}" />
    <meta name="description" content="View Inspection Guarantee pricing details for {year} {make} {model}" />
    <meta name="robots" content="noindex,nofollow" />
    <meta name="googlebot" content="noindex,nofollow" />
    <meta name="google" content="nosnippet" />
    <meta name="bingbot" content="noindex,nofollow" />
    <meta name="twitter:title" content="Inspection Guarantee Pricing | {year} {make} {model}" />
    <meta name="twitter:description" content="View Inspection Guarantee pricing details for {year} {make} {model}" />
    <meta name="twitter:image" content={ogImage} />
    <meta property="og:image" content={ogImage} />
    <meta property="og:image:secure_url" content={ogImage} />
    <meta property="og:image:type" content="image/png" />
</svelte:head>

<div class="form-container w-screen mx-auto items-top text-center align-middle justify-center">
    <div class="flex p-12 items-top flex-col lg:flex-row justify-center mx-auto w-full">
        <!-- Vehicle Details Card -->
        <div class="flex flex-col gap-2">
            <PriceCard title={orderTypeToEnglish(order_type_id)} annualPrice={pricing?.cents_annually / 100} monthlyPrice={pricing?.cents_monthly / 100} inspectionPrice={pricing?.inspectionPrice}>
                <div class="flex flex-col">
                    <div class="mt-2 font-medium flex flex-col w-full text-center justify-center align-middle mx-auto text-lg">
                        <p>{year} {make} {model}</p>
                        <hr class="w-full" />
                    </div>
                    <div class="flex flex-row w-full text-center justify-center align-middle mx-auto text-md tracking-tight text-surface-700">
                        <p>Lemon Squad Order ID:</p>
                        <p>{order_id}</p>
                    </div>
                </div>
            </PriceCard>
        </div>

        <!-- Signup Form -->
        {#if step === 1}
            <div class="flex w-full flex-col card p-4 rounded-xl">
                <div class="flex flex-col gap-2">
                    <h2 class="h2">Signup</h2>
                    <p>Please fill out the form below to signup for the program</p>
                </div>
                <form class="flex flex-col gap-4 w-full mt-4">
                    <input type="text" placeholder="First Name" class="input" bind:value={$orderFormStore.c_fname} />
                    <input type="text" placeholder="Last Name" class="input" bind:value={$orderFormStore.c_lname} />
                    <input type="tel" placeholder="Phone Number" class="input" bind:value={$orderFormStore.seller_phone1} />
                    <input type="email" placeholder="Email Address" class="input" bind:value={$orderFormStore.c_email} />
                    <input type="text" placeholder="Address Line 1" class="input" bind:value={$orderFormStore.vehicle_address1} />
                    <input type="text" placeholder="Address Line 2 (Optional)" class="input" bind:value={$orderFormStore.vehicle_address2} />
                    <div class="grid grid-cols-2 gap-4">
                        <input type="text" placeholder="City" class="input" bind:value={$orderFormStore.vehicle_city} />
                        <input type="text" placeholder="State" class="input" bind:value={$orderFormStore.vehicle_state} />
                    </div>
                    <input type="text" placeholder="ZIP Code" class="input" bind:value={$orderFormStore.vehicle_zip} />
                    <button class="btn btn-primary w-full bg-tertiary-500 text-white text-lg" on:click={() => (step = 2)}> Continue </button>
                </form>
            </div>
        {/if}

        <!-- Order Summary -->
        {#if step === 2}
            <div class="flex flex-col mx-auto">
                <OrderSummary>
                    <div class="flex items-start gap-4 bg-surface-200 p-4 rounded-xl">
                        <label for="confirmCar" class="text-sm">
                            <p class=" border-b-2 pb-2 mb-2 w-full text-left 0">I want to enroll in the Inspection Guarantee for this vehicle</p>

                            <div class="flex flex-row text-left items-center">
                                <input type="checkbox" id="confirmCar" class="checkbox border-2 border-primary-400 w-6 h-6 mt-1" bind:checked={$orderFormStore.carConfirmed} />
                                <div class="flex flex-col text-left w-full mx-auto text-center">
                                    <p class="font-medium mb-1">{year} {make} {model}</p>
                                    <p class="text-xs text-gray-600">VIN: {order.vin}</p>
                                </div>
                            </div>
                        </label>
                    </div>
                    <button class="btn btn-primary w-full bg-tertiary-500 text-white text-lg" on:click={handleCheckout}> Checkout </button>

                    <div class="flex flex-col gap-2">
                        <p class="text-xs">
                            By clicking "Checkout" you agree to the
                            <a href="/terms" target="_blank" rel="noreferrer" class="text-primary-400 border-b-2"> Terms of Service </a>
                            and
                            <a href="/privacy" target="_blank" rel="noreferrer" class="text-primary-400 border-b-2"> Privacy Policy </a>
                        </p>
                    </div>
                </OrderSummary>
                <div class="mt-4">
                    <button class="btn btn-secondary w-full text-lg bg-surface-500 text-black" on:click={() => (step = 1)}> Back </button>
                </div>
            </div>
        {/if}
    </div>
</div>

<!-- Questions Section -->
<div class="flex flex-col p-5 lg:mx-auto w-full bg-primary-500 py-10">
    {#if !formSubmitted}
        <div class="bg-white rounded-2xl p-10 gap-4 lg:max-w-1/2 min-w-sm lg:max-w-lg flex-col justify-center mx-auto w-full">
            <h2 class="h2 text-center text-3xl">Have Questions?</h2>
            <p class="p-4 text-center">Enter your information below to get in touch with us about the program and how it works.</p>

            <form class="flex-col flex gap-2 w-full">
                <div class="grid grid-cols-1 gap-2">
                    <input type="text" placeholder="Enter your name" class="input px-4" bind:value={$infoRequestStore.c_name} />
                    <input type="text" placeholder="Enter your email" class="input px-4" bind:value={$infoRequestStore.c_email} />
                    <input type="text" placeholder="Enter your phone number" class="input px-4" bind:value={$infoRequestStore.c_phone} />
                    <textarea class="input" rows="4" placeholder="Enter any additional information you'd like to us to know" bind:value={$infoRequestStore.message}></textarea>
                </div>

                <input type="hidden" bind:value={order_id} />
                <button type="submit" class="btn variant-filled-tertiary mt-4 text-white" on:click={submitInfoRequestForm}> Request More Information </button>
            </form>
            <div class="p-4 flex">
                <p class="w-full text-center align-text-top leading-tight text-xs">
                    By clicking "Continue", I understand that I will receive emails, texts or phone calls regarding my inquiry into this program. I have read and agree to the
                    <a href="/privacy" target="_blank" rel="noreferrer" class="text-primary-400 border-b-2"> Privacy Policy </a>
                </p>
            </div>
        </div>
    {:else}
        <div class="card rounded-xl p-10 max-w-1/2 min-w-sm max-w-lg flex-col justify-center mx-auto w-full">
            <div class="flex flex-col gap-5 w-full text-center justify-center align-middle mx-auto text-[50px]">
                <h2 class="h2">Thank You!</h2>
                <span class="text-xl">Your request has been submitted ✅</span>
            </div>
        </div>
    {/if}
</div>

<RepairCards />
<PaymentCards />
<WhyC2 />
<CTAImageSection />
<Testimoninals />

<style>
    .input {
        border: 1px solid #e2e8f0;
        border-radius: 0.5rem;
        padding: 0.75rem 1rem;
        width: 100%;
        font-size: 1rem;
        line-height: 1.5;
        color: #1a202c;
        background-color: #f4f4f4;
        transition:
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;

        &:focus {
            @apply border-primary-500;
            outline: none;
            box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
        }

        &::placeholder {
            color: #a0aec0;
        }

        &:disabled {
            background-color: #edf2f7;
            cursor: not-allowed;
        }
    }

    .form-container {
        background: url("$lib/assets/images/order/hero-bg-noimg.jpg");
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        background-origin: border-box;
    }
</style>
